import { useEffect, useRef, useState } from 'react'
import {
  choiceBounds, isMultiSelect, isQuestionVisible, validateAnswer, withProfileDemographics,
  type ApplicationRecord, type ApplicationTemplate, type TemplateQuestion
} from '@romain130492/topchopsticks'
import { api } from '../lib/api'
import { useI18n } from '../hooks/useI18n'
import { setApplicationLocally, setProfileLocally, useSession } from '../hooks/useSession'
import { showToast } from '../lib/toast'
import { clearDraft, readDraft, writeDraft } from '../lib/onboardingDraft'
import Button from '../components/Button'
import LoadingState from '../components/LoadingState'
import QuestionControl, { autoAdvances, isLongControl } from '../components/onboarding/QuestionControl'
import ProfileSetupPanel, { needsProfileSetup } from '../components/onboarding/ProfileSetupPanel'

/**
 * The questionnaire — one question per screen, matching the mini program.
 *
 * VALIDATE PER QUESTION, NEVER ONLY AT THE END. Every answer is checked on the
 * way out of its own screen and the flow refuses to advance until it is right.
 * Before this, nothing was checked until the final submit, so twenty screens
 * of work ended in one opaque 500 with no indication of which answer was
 * wrong — and because the type lookup was broken, the wrong answers were
 * every multi-select on the form.
 *
 * Two other behaviours ported deliberately, both of which were bugs first:
 *  - Answers save after EVERY question, so quitting half-way loses nothing.
 *  - The resume index is computed ONCE. Recomputing it whenever the saved
 *    application changes sends you backwards mid-flow, because a save that
 *    lands late carries an older set of answers.
 */
export default function OnboardingFlow({
  onExit, onComplete
}: { onExit: () => void; onComplete: () => void }) {
  const { t, L, lang } = useI18n()
  const { application, profile } = useSession()

  const [template, setTemplate] = useState<ApplicationTemplate | null>(null)
  const [answers, setAnswers] = useState<Record<string, unknown>>({})
  const [index, setIndex] = useState(0)
  const [saving, setSaving] = useState(false)
  const [failed, setFailed] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Sticky for this mount: once the panel is done it must not reappear because
  // the profile refetch lands a moment later.
  const [setupDone, setSetupDone] = useState(false)

  // Hooks BEFORE any early return — a hook below one runs on some renders and
  // not others, which is React error #310 and a blank screen.
  const bootstrapped = useRef(false)
  const saveChain = useRef<Promise<unknown>>(Promise.resolve())
  const applicationId = useRef<string | null>(application?.id || null)
  const profileIdRef = useRef<string | null>(profile?.id || null)

  useEffect(() => {
    let active = true
    // The profile is REQUIRED for the seeding below, so wait for it rather
    // than reading whatever the session happens to hold at mount. If it has
    // not arrived yet, the demographics merge silently does nothing and the
    // member is asked their gender again — the exact bug this fixes.
    const profilePromise = profile
      ? Promise.resolve(profile)
      : api.getMe().catch(() => null)

    Promise.all([
      api.getApplicationTemplate(),
      api.getMyApplication().catch(() => null),
      profilePromise
    ])
      .then(([loadedTemplate, existing, loadedProfile]) => {
        if (!active) return
        // Refuse the organize-a-table template outright: rendering it here is
        // how a new member ended up being asked to name a dinner table.
        if (loadedTemplate?.template_kind && loadedTemplate.template_kind !== 'application') {
          console.error('[tc][template] WRONG TEMPLATE', loadedTemplate.template_kind)
          setFailed(true)
          return
        }
        setTemplate(loadedTemplate)
        if (existing?.id) applicationId.current = existing.id
        if (!bootstrapped.current) {
          bootstrapped.current = true
          // SEED FROM THE PROFILE TOO, not just from application.answers.
          //
          // gender / date_of_birth / industry / origin are answers AND profile
          // columns — the backend copies them across in syncDemographics. A
          // resume that reads only `answers` cannot see them, so "first
          // question with no key in answers" landed straight back on gender:
          // pick Man, next, refresh, get asked again.
          const profileId = (loadedProfile as { id?: string } | null)?.id || null
          profileIdRef.current = profileId

          // THREE SOURCES, weakest first.
          //   profile demographics — gender/dob/industry/origin live on the
          //     profile as well as in answers.
          //   server answers       — the last successfully saved draft.
          //   LOCAL DRAFT          — written on every change, so it is at
          //     least as new as the server copy and survives a save that
          //     never reached the backend. It therefore wins.
          const saved = {
            ...withProfileDemographics(
              existing?.answers as Record<string, unknown>,
              loadedProfile as unknown as Record<string, unknown>
            ),
            ...readDraft(profileId)
          }
          setAnswers(saved)

          // Resume against the SAME list the render uses. This used to walk
          // the unfiltered template while `index` indexed the filtered one, so
          // a single hidden question knocked every position out by one and
          // dropped you on the wrong screen.
          const steps = flatten(loadedTemplate).filter((q) => isQuestionVisible(q, saved))
          const next = steps.findIndex((q) => !Object.prototype.hasOwnProperty.call(saved, q.key))
          setIndex(next < 0 ? Math.max(0, steps.length - 1) : next)
        }
      })
      .catch(() => { if (active) setFailed(true) })
    return () => { active = false }
  }, [])

  const steps = flatten(template).filter((q) => isQuestionVisible(q, answers))
  const question: TemplateQuestion | undefined = steps[index]
  const lastStep = index >= steps.length - 1

  // Every question is a fresh screen: arriving scrolled down hides the title.
  useEffect(() => { window.scrollTo({ top: 0 }) }, [index])
  // A new question must never inherit the previous one's error message.
  useEffect(() => { setError(null) }, [index])

  /**
   * Set answers AND write the local draft, together, always.
   *
   * Every call site goes through this so none of them can update the screen
   * without also recording the answer somewhere that survives a refresh.
   */
  const commitAnswers = (next: Record<string, unknown>) => {
    setAnswers(next)
    writeDraft(profileIdRef.current, next)
  }

  /** Serialised so two quick answers cannot both POST and create two rows. */
  const persist = (next: Record<string, unknown>, finish: boolean) => {
    saveChain.current = saveChain.current.catch(() => {}).then(async () => {
      const id = applicationId.current
      const payload: ApplicationRecord = {
        id: id || undefined,
        application_template_id: template?.id,
        answers: next,
        status: finish ? 'submitted' : 'draft'
      }
      const saved = id ? await api.updateMyApplication(payload) : await api.createMyApplication(payload)
      if (saved?.id) applicationId.current = saved.id
      setApplicationLocally({ ...payload, id: saved?.id || id || undefined })
    })
    return saveChain.current
  }

  /**
   * Leave this question — but only with an answer the backend will accept.
   *
   * `skip` is for optional questions and bypasses the check by storing null,
   * which is a deliberate non-answer rather than an invalid one.
   */
  const advance = (value: unknown, skip = false) => {
    if (!question || saving) return

    if (!skip) {
      const reason = validateAnswer(question, value, lang)
      if (reason) {
        setError(reason)
        showToast({ title: reason, icon: 'none' })
        return
      }
    }
    setError(null)

    const next = { ...answers, [question.key]: skip ? null : value }
    commitAnswers(next)

    if (!lastStep) {
      setIndex((i) => i + 1)
      // Not awaited: moving between questions must stay instant. A failure is
      // harmless because the final save sends the complete set.
      persist(next, false).catch((e) => console.warn('[tc][onboarding] progress save failed', e))
      return
    }

    setSaving(true)
    persist(next, true)
      .then(() => api.updateOnboarding('completed'))
      .then((updated) => {
        // Only now is the server copy authoritative.
        clearDraft(profileIdRef.current)
        setProfileLocally(updated)
        onComplete()
      })
      .catch((err) => {
        // Never swallow the final submit's reason: it is the one request whose
        // failure the member cannot work around by trying a different answer.
        console.error('[tc][onboarding] final submit failed', err)
        const message = (err as { data?: { message?: string }; message?: string })?.data?.message
          || (err as { message?: string })?.message
          || t('onboarding.saveFailed')
        setError(message)
        showToast({ title: message, icon: 'none' })
      })
      .finally(() => setSaving(false))
  }

  if (failed) return <div className="py-10 text-center text-muted">{t('onboarding.loadFailed')}</div>

  // STEP ZERO: name and picture, before any questionnaire screen.
  //
  // The mini program asks for these in the link gate, from WeChat. The web has
  // no WeChat to ask, so it asks the member — but it must happen at the SAME
  // point in the flow, or a web member reaches the tab bar as `member_23d34d`
  // with a placeholder photo and no way to notice.
  //
  // Skipped automatically for anyone who already did it, including someone who
  // signed up on WeChat and is now opening the web app.
  if (!setupDone && needsProfileSetup(profile as never)) {
    return <ProfileSetupPanel onDone={() => setSetupDone(true)} />
  }

  if (!template || !question) return <LoadingState />

  const selected = answers[question.key]
  const required = Boolean(question.is_required)
  const { min, max } = choiceBounds(question)
  const auto = autoAdvances(question)
  const long = isLongControl(question)

  // One support line only, so the options stay above the fold. The count rule
  // wins over generic help text because it is the thing you cannot guess.
  const countLine = isMultiSelect(question)
    ? (lang === 'zh'
        ? `选择 ${min || 1}${max ? ` 至 ${max}` : ''} 项`
        : (max ? `Select up to ${max}` : `Select at least ${min || 1}`))
    : null

  const continueLabel = lastStep ? (lang === 'zh' ? '完成' : 'Finish') : t('common.continue')

  const footer = (
    <div className="mt-5 space-y-2">
      <Button loading={saving} onClick={() => advance(selected)}>{continueLabel}</Button>
      {!required ? (
        <button
          type="button"
          onClick={() => advance(null, true)}
          className="w-full py-2 text-center text-13 text-muted-2"
        >
          {lang === 'zh' ? '跳过' : 'Skip'}
        </button>
      ) : null}
    </div>
  )

  /**
   * The shortcut above a long control, matching the mini program's
   * `qflow-inline-actions`.
   *
   * TWO COLUMNS, LEFT DELIBERATELY EMPTY. The restaurant and country pickers
   * scroll far past the fold, so without this there is no visible way forward
   * after picking. But it must not read as a second primary action competing
   * with the real footer button — hence half width, on the right, and outline
   * rather than solid gold: white text, white border, dark inside, small.
   */
  const inlineActions = (
    <div className="mt-4 grid grid-cols-2 gap-2">
      <div />
      <div className="space-y-1.5">
        {/* Same Button as the footer, so it SPINS. This was a raw <button>
            with only `disabled`, so pressing Finish here — the copy people
            actually reach on a long control — gave no feedback at all while
            the final submit ran, and looked dead. */}
        <Button
          size="row"
          variant="ghost"
          loading={saving}
          className="w-full border-ivory text-ivory"
          onClick={() => advance(selected)}
        >
          {continueLabel}
        </Button>
        {!required ? (
          <button
            type="button"
            onClick={() => advance(null, true)}
            className="w-full rounded-pill border border-ivory bg-transparent px-3 py-2 text-13 font-bold tracking-[0.04em] text-ivory"
          >
            {lang === 'zh' ? '跳过' : 'Skip'}
          </button>
        ) : null}
      </div>
    </div>
  )

  return (
    <div>
      <div className="flex items-center justify-between py-2">
        {/* At question 1 back means LEAVE. Nobody is forced to finish. */}
        <button type="button" onClick={() => (index > 0 ? setIndex(index - 1) : onExit())} className="text-h2 text-gold">←</button>
        <span className="text-sm text-muted">{index + 1} / {steps.length}</span>
      </div>
      <div className="h-0.5 w-full rounded-pill bg-gold-faint">
        <div className="h-full rounded-pill bg-gold transition-all" style={{ width: `${((index + 1) / steps.length) * 100}%` }} />
      </div>

      <h1 className="mt-6 font-display text-hero leading-tight text-ivory">{L(question.label as never)}</h1>

      {long && !auto ? inlineActions : null}

      {countLine
        ? <p className="mt-2 text-13 text-muted">{countLine}</p>
        : (question.help_text ? <p className="mt-2 text-13 leading-relaxed text-muted">{L(question.help_text as never)}</p> : null)}

      <div className="mt-5">
        <QuestionControl
          question={question}
          value={selected}
          onChange={(value) => { setError(null); commitAnswers({ ...answers, [question.key]: value }) }}
          onCommit={(value) => { commitAnswers({ ...answers, [question.key]: value }); advance(value) }}
        />
      </div>

      {error ? <p className="mt-3 text-13 text-[#e08a6a]">{error}</p> : null}

      {/* Single choice and country auto-advance, so they need no footer. */}
      {!auto ? footer : null}
    </div>
  )
}

const flatten = (template: ApplicationTemplate | null): TemplateQuestion[] =>
  (template?.sections || []).flatMap((section) => section.questions || [])
